import React from "react";
import { Link } from "react-router-dom";
import { Users, ArrowUpRight } from "lucide-react";
import useAdminInstitution from "Explore/Hooks/useAdminInstitution";
import styles from "./CollaborationLink.module.scss";
import classNames from "classnames";

/**
 * CollaborationLink - link to the Collaboration page for a specific entity.
 * Only rendered when the current user is an admin.
 *
 * @param {Object} props
 * @param {Object} props.entity - The entity object { id, name, type, photo, avatar, ... }
 * @param {string} [props.variant="default"] - "default" | "light" | "outline"
 * @param {string} [props.label="View Collaborations"] - Display text
 * @param {string} [props.className] - Additional class name
 * @param {boolean} [props.showIcon=true] - Whether to show the Users icon
 * @param {boolean} [props.showArrow=true] - Whether to show the ArrowUpRight icon
 */
const CollaborationLink = ({
  entity,
  variant = "default",
  label = "View Collaborations",
  className,
  showIcon = true,
  showArrow = true,
}) => {
  const { isAdmin } = useAdminInstitution();

  if (!isAdmin || !entity?.id) {
    return null;
  }

  const entityType = entity.type || "institution";
  const toUrl = `/explore/collaborations?entity_id=${entity.id}&entity_type=${entityType}`;

  return (
    <Link
      to={toUrl}
      state={{ entity: { ...entity, type: entityType } }}
      className={classNames(
        styles["collaboration-link"],
        styles[`variant-${variant}`],
        className
      )}
      title={`View collaboration analysis for ${entity.name || "this entity"}`}
      data-testid="collaboration-link"
    >
      {showIcon && <Users size={15} className={styles["icon"]} />}
      <span className={styles["text"]}>{label}</span>
      {showArrow && <ArrowUpRight size={14} className={styles["arrow"]} />}
    </Link>
  );
};

export default CollaborationLink;
